import React, { useState, useCallback } from 'react'
import { Box, Tabs, Tab, Alert } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DrawIcon from '@mui/icons-material/Draw'
import FileUploader from './FileUploader'
import TextPaster from './TextPaster'
import MapDrawer from './MapDrawer'
import { convertToWGS84 } from '../../utils/geojsonHelpers'

/**
 * Valida que un objeto sea un GeoJSON válido
 */
const validateGeoJSON = geojson => {
    if (!geojson) return { valid: false, error: 'GeoJSON vacío' }

    try {
        const parsed = typeof geojson === 'string' ? JSON.parse(geojson) : geojson
        const validTypes = [
            'FeatureCollection', 'Feature', 'Polygon', 'MultiPolygon',
            'Point', 'LineString', 'MultiPoint', 'MultiLineString', 'GeometryCollection'
        ]

        if (!parsed.type || !validTypes.includes(parsed.type)) {
            return { valid: false, error: 'Tipo de GeoJSON no válido' }
        }

        return { valid: true, parsed }
    } catch (err) {
        return { valid: false, error: 'JSON inválido: ' + err.message }
    }
}

/**
 * Componente para ingresar GeoJSON con múltiples opciones
 * @param {Object} props
 * @param {string} props.value - Valor actual del GeoJSON (string)
 * @param {Function} props.onChange - Callback cuando cambia el valor
 * @param {string} props.error - Error de validación externo
 */
const GeoJSONInput = ({ value, onChange, error: externalError, referenceGeoJSON, referenceCenter, referenceZoom }) => {
    const [activeTab, setActiveTab] = useState(0)
    const [internalError, setInternalError] = useState(null)

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue)
        setInternalError(null)
    }

    const handleGeoJSONChange = useCallback((geojsonValue) => {
        const validation = validateGeoJSON(geojsonValue)

        if (validation.valid) {
            setInternalError(null)

            // Intentar convertir coordenadas a WGS84 si es necesario (soporte UTM)
            const converted = convertToWGS84(validation.parsed)

            // Siempre guardar como string
            const stringValue = JSON.stringify(converted, null, 2)
            onChange(stringValue)
        } else {
            setInternalError(validation.error)
            onChange('')
        }
    }, [onChange])

    const displayError = internalError || externalError

    return (
        <Box sx={{ width: '100%' }}>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        minHeight: 48,
                        textTransform: 'none',
                    },
                }}
            >
                <Tab
                    icon={<CloudUploadIcon />}
                    iconPosition="start"
                    label="Cargar Archivo"
                />
                <Tab
                    icon={<ContentPasteIcon />}
                    iconPosition="start"
                    label="Pegar JSON"
                />
                <Tab
                    icon={<DrawIcon />}
                    iconPosition="start"
                    label="Dibujar en Mapa"
                />
            </Tabs>

            {displayError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {displayError}
                </Alert>
            )}

            <Box sx={{ pt: 2 }}>
                {activeTab === 0 && (
                    <FileUploader
                        onFileLoad={handleGeoJSONChange}
                        currentValue={value}
                    />
                )}

                {activeTab === 1 && (
                    <TextPaster
                        value={value}
                        onChange={handleGeoJSONChange}
                    />
                )}

                {activeTab === 2 && (
                    <MapDrawer
                        onDrawComplete={handleGeoJSONChange}
                        initialValue={value}
                        referenceGeoJSON={referenceGeoJSON}
                        referenceCenter={referenceCenter}
                        referenceZoom={referenceZoom}
                    />
                )}
            </Box>

            {value && !displayError && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    GeoJSON válido cargado correctamente
                </Alert>
            )}
        </Box>
    )
}

export default GeoJSONInput
